import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogActions,
  Box, Stack, Typography, Chip, Button, IconButton, Tooltip,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Avatar, Divider, FormControlLabel, Checkbox,
  Accordion, AccordionSummary, AccordionDetails,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HandshakeIcon from "@mui/icons-material/Handshake";
import NotesIcon from "@mui/icons-material/Notes";
import HistoryIcon from "@mui/icons-material/History";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import StarIcon from "@mui/icons-material/Star";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import VoicemailIcon from "@mui/icons-material/Voicemail";
import ReplayIcon from "@mui/icons-material/Replay";
import BlockIcon from "@mui/icons-material/Block";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PremiumDateTimePicker from "../../Components/PremiumDateTimePicker";
import {
  updateTask, logContact, fetchTaskAttempts,
  type Task, type LeadContact,
  PRIORITY_CONFIG, FOLLOW_UP_OUTCOME_OPTIONS,
} from "./taskApi";
import type { TeamUser } from "../LeadSystem/leadApi";

/* ── DCC design tokens ────────────────────────────────────────────────── */
const NAVY        = "#0d1b2a";
const NAVY_LIGHT  = "#1e3a5f";
const GOLD        = "#c9a84c";
const SOFT_BG     = "#f6f7fb";
const BORDER_SOFT = "#e5e7eb";

/* ── Local helpers ─────────────────────────────────────────────────────── */
function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso as string;
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso as string;
  const diff = Date.now() - t;
  const abs  = Math.abs(diff);
  const min  = Math.round(abs / 60_000);
  if (min < 1)        return diff >= 0 ? "just now" : "in a moment";
  if (min < 60)       return diff >= 0 ? `${min} min ago` : `in ${min} min`;
  const hr = Math.round(min / 60);
  if (hr < 24)        return diff >= 0 ? `${hr}h ago`    : `in ${hr}h`;
  const d  = Math.round(hr / 24);
  return diff >= 0 ? `${d}d ago` : `in ${d}d`;
}

function displayName(
  u: { username?: string; first_name?: string | null; last_name?: string | null; display_name?: string | null } | null | undefined
): string {
  if (!u) return "Unassigned";
  return (
    u.display_name ||
    [u.first_name, u.last_name].filter(Boolean).join(" ") ||
    u.username ||
    "Unassigned"
  );
}

function initialsOf(name: string): string {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .map((s) => s[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function statusChipStyle(status: Task["status"]) {
  const map = {
    pending:     { label: "Pending",     bg: "#fef3c7", fg: "#b45309" },
    in_progress: { label: "In Progress", bg: "#dbeafe", fg: "#2563eb" },
    completed:   { label: "Completed",   bg: "#dcfce7", fg: "#16a34a" },
    cancelled:   { label: "Cancelled",   bg: "#f3f4f6", fg: "#6b7280" },
  } as const;
  return map[status] ?? map.pending;
}

const TYPE_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  call:    { label: "Call",    icon: <PhoneIcon     sx={{ fontSize: 18 }} />, color: "#3b82f6" },
  email:   { label: "Email",   icon: <EmailIcon     sx={{ fontSize: 18 }} />, color: "#8b5cf6" },
  meeting: { label: "Meeting", icon: <HandshakeIcon sx={{ fontSize: 18 }} />, color: "#10b981" },
  note:    { label: "Note",    icon: <NotesIcon     sx={{ fontSize: 18 }} />, color: "#f59e0b" },
};

const OUTCOME_META: Record<string, { label: string; icon: React.ReactNode; color: string; tone: "positive"|"neutral"|"negative" }> = {
  contacted:          { label: "Contacted",          icon: <CheckCircleIcon sx={{ fontSize: 16 }} />, color: "#16a34a", tone: "positive" },
  no_answer:          { label: "No Answer",          icon: <EventBusyIcon   sx={{ fontSize: 16 }} />, color: "#f59e0b", tone: "neutral"  },
  voicemail:          { label: "Voicemail",          icon: <VoicemailIcon   sx={{ fontSize: 16 }} />, color: "#3b82f6", tone: "neutral"  },
  interested:         { label: "Interested",         icon: <StarIcon        sx={{ fontSize: 16 }} />, color: "#0d9488", tone: "positive" },
  not_interested:     { label: "Not Interested",     icon: <BlockIcon       sx={{ fontSize: 16 }} />, color: "#ef4444", tone: "negative" },
  callback_requested: { label: "Callback Requested", icon: <ReplayIcon      sx={{ fontSize: 16 }} />, color: "#8b5cf6", tone: "neutral"  },
  email_sent:         { label: "Email Sent",         icon: <EmailIcon       sx={{ fontSize: 16 }} />, color: "#0ea5e9", tone: "positive" },
  meeting_scheduled:  { label: "Meeting Scheduled",  icon: <CalendarMonthIcon sx={{ fontSize: 16 }} />, color: "#22c55e", tone: "positive" },
};

function outcomeMeta(outcome: string | null | undefined) {
  if (!outcome) return null;
  return OUTCOME_META[outcome] ?? { label: outcome.replace(/_/g, " "), icon: null, color: "#64748b", tone: "neutral" as const };
}

const AVATAR_BG = ["#6366f1","#ec4899","#f59e0b","#10b981","#3b82f6","#8b5cf6","#ef4444","#06b6d4"];
function avatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_BG[h % AVATAR_BG.length];
}

interface Props {
  open: boolean;
  task: Task | null;
  teamUsers?: TeamUser[];
  onClose: () => void;
  onChanged: () => void;
}

/**
 * Premium follow-up workflow dialog (UI redesign — backend unchanged).
 *
 *   1. Navy gradient hero — lead identity + AI/task/lead chips + stats
 *   2. Activity timeline  — coloured-rail attempt cards, newest first
 *   3. Log next attempt   — segmented method picker + outcome grid
 *      + contextual workflow guidance + optional reschedule
 *   4. Task settings      — collapsed accordion (administrative, muted)
 *   5. Footer hierarchy   — Complete / Cancel / Save / Close
 */
export default function TaskDetailDialog({ open, task, teamUsers, onClose, onChanged }: Props) {
  const navigate = useNavigate();

  /* ── Edit drafts (Section 4 / footer Save) ──────────────────────────── */
  const [dueAt,      setDueAt]      = useState("");
  const [priority,   setPriority]   = useState<Task["priority"]>("medium");
  const [assignedTo, setAssignedTo] = useState<number | "">("");
  const [notesDraft, setNotesDraft] = useState("");
  const [saving,     setSaving]     = useState(false);
  const [errorMsg,   setErrorMsg]   = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  /* ── Section 3 "Log new attempt" ───────────────────────────────────── */
  const [attemptType,         setAttemptType]         = useState<"call"|"email"|"meeting"|"note">("call");
  const [attemptOutcome,      setAttemptOutcome]      = useState("");
  const [attemptNotes,        setAttemptNotes]        = useState("");
  const [attemptScheduleNext, setAttemptScheduleNext] = useState(false);
  const [attemptNextDue,      setAttemptNextDue]      = useState("");
  const [logging,             setLogging]             = useState(false);

  /* ── Section 2 attempts state ──────────────────────────────────────── */
  const [attempts,        setAttempts]        = useState<LeadContact[]>([]);
  const [attemptsLoading, setAttemptsLoading] = useState(false);

  useEffect(() => {
    if (!task) return;
    setDueAt(task.due_at ?? "");
    setPriority(task.priority);
    setAssignedTo(task.assigned_to ?? "");
    setNotesDraft(task.notes ?? "");
    setErrorMsg(null);
    setSettingsOpen(false);
    setAttemptType("call");
    setAttemptOutcome("");
    setAttemptNotes("");
    setAttemptScheduleNext(false);
    setAttemptNextDue("");

    if (Array.isArray(task.attempts)) {
      setAttempts(task.attempts);
    } else if (open) {
      setAttemptsLoading(true);
      fetchTaskAttempts(task.id)
        .then(setAttempts)
        .catch(() => setAttempts([]))
        .finally(() => setAttemptsLoading(false));
    }
  }, [task?.id, open]);

  // Derived values — hooks BEFORE any early return.
  const lastActivity = task?.last_activity_at ?? attempts[0]?.contacted_at ?? null;

  const summary = useMemo(() => {
    if (!task) {
      return {
        leadName: "—", leadStatus: null as string | null, aiScore: null as string | null,
        assignee: "Unassigned", completer: "Unassigned",
        priority: { color: "#64748b", label: "Medium" },
        dueAt: "—", completedAt: "—", lastActAt: "—",
        attemptCount: 0,
      };
    }
    return {
      leadName:    task.lead?.name ?? "—",
      leadStatus:  task.lead?.status ?? null,
      aiScore:     (task.lead as any)?.ai_score ?? null,
      assignee:    displayName(task.assignee as any),
      completer:   displayName(task.completer as any),
      priority:    PRIORITY_CONFIG[task.priority] ?? { color: "#64748b", label: task.priority },
      dueAt:       formatDateTime(task.due_at),
      completedAt: formatDateTime(task.completed_at),
      lastActAt:   formatDateTime(lastActivity),
      attemptCount: task.follow_up_attempts ?? attempts.length,
    };
  }, [task, lastActivity, attempts.length]);

  if (!task) return null;

  const isFinal     = task.status === "completed" || task.status === "cancelled";
  const statusStyle = statusChipStyle(task.status);

  const isDirty =
    dueAt !== (task.due_at ?? "") ||
    priority !== task.priority ||
    (assignedTo === "" ? null : Number(assignedTo)) !== task.assigned_to ||
    notesDraft !== (task.notes ?? "");

  const run = async (patch: Partial<Task>) => {
    setSaving(true);
    setErrorMsg(null);
    try {
      await updateTask(task.id, patch);
      onChanged();
      onClose();
    } catch (e: any) {
      setErrorMsg(e?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleLogAttempt = async () => {
    if (!task.lead_id) { setErrorMsg("This task is not linked to a lead."); return; }
    setLogging(true);
    setErrorMsg(null);
    try {
      await logContact({
        lead_id:           task.lead_id,
        task_id:           task.id,
        type:              attemptType,
        follow_up_outcome: attemptOutcome || undefined,
        notes:             attemptNotes || undefined,
      });
      if (attemptScheduleNext && attemptNextDue) {
        await updateTask(task.id, { due_at: attemptNextDue } as Partial<Task>);
      }
      onChanged();
    } catch (e: any) {
      setErrorMsg(e?.message ?? "Log attempt failed");
    } finally {
      setLogging(false);
    }
  };

  /* ── Smart workflow guidance — keyed on outcome tone ───────────────── */
  const omSel = outcomeMeta(attemptOutcome);
  const guidance = (() => {
    if (!omSel) return null;
    if (omSel.tone === "positive") {
      if (attemptOutcome === "meeting_scheduled" || attemptOutcome === "interested" || attemptOutcome === "contacted") {
        return { tone: "positive" as const,
          text: "Positive outcome — log this attempt, then consider Complete Follow-up below to close the thread.",
          suggestReschedule: false };
      }
      return { tone: "positive" as const, text: "Email logged.", suggestReschedule: false };
    }
    if (omSel.tone === "neutral") {
      if (attemptOutcome === "callback_requested") {
        return { tone: "neutral" as const,
          text: "Callback requested — set the next callback time below.",
          suggestReschedule: true };
      }
      return { tone: "neutral" as const,
        text: "No reach — reschedule the follow-up to try again later.",
        suggestReschedule: true };
    }
    return { tone: "negative" as const,
      text: "Lead is not interested — consider Cancel Follow-up after logging this attempt.",
      suggestReschedule: false };
  })();

  /* ──────────────────────────────────────────────────────────────────── */

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", bgcolor: SOFT_BG } }}
    >
      {/* ═════ SECTION 1 · PREMIUM HEADER ═════════════════════════════ */}
      <Box
        sx={{
          px: 3, py: 2.3,
          background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`,
          color: "#fff",
        }}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <Avatar sx={{
            width: 52, height: 52, fontWeight: 800, fontSize: 18,
            bgcolor: avatarColor(summary.leadName),
            color: "#fff",
            border: `2px solid ${GOLD}55`,
          }}>
            {initialsOf(summary.leadName)}
          </Avatar>

          <Box flex={1} minWidth={0}>
            <Typography variant="caption" sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1.2, opacity: 0.7, textTransform: "uppercase" }}>
              Follow-up · Task #{task.id}
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15, textTransform: "capitalize", mt: 0.2 }}>
              {summary.leadName}
            </Typography>
            <Stack direction="row" gap={0.7} flexWrap="wrap" mt={0.6} alignItems="center">
              {summary.leadStatus && (
                <Chip size="small" label={summary.leadStatus.replace(/_/g, " ")} sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: "rgba(255,255,255,0.16)", color: "#fff", textTransform: "capitalize" }} />
              )}
              {summary.aiScore && (
                <Chip size="small" icon={<AutoAwesomeIcon sx={{ fontSize: "12px !important", color: `${GOLD} !important` }} />}
                  label={summary.aiScore} sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: "rgba(201,168,76,0.18)", color: GOLD, border: `1px solid ${GOLD}55`, textTransform: "uppercase" }} />
              )}
              <Chip size="small" label={statusStyle.label}
                sx={{ height: 20, fontSize: 10.5, fontWeight: 800, bgcolor: statusStyle.bg, color: statusStyle.fg }} />
              <Chip size="small" label={summary.priority.label}
                sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: `${summary.priority.color}26`, color: "#fff", border: `1px solid ${summary.priority.color}99` }} />
              {task.lead && (
                <Chip
                  size="small"
                  icon={<OpenInNewIcon sx={{ fontSize: "12px !important", color: "#fff !important" }} />}
                  // Carry the public lead number into the chip when available
                  // so the task header doubles as a permanent business
                  // reference — clicking still opens the lead.
                  label={task.lead.lead_number ?? "Open lead"}
                  onClick={() => { onClose(); navigate(`/leads/${task.lead!.id}`); }}
                  sx={{
                    height: 20, fontSize: 10.5, fontWeight: 700, cursor: "pointer",
                    fontFamily: task.lead.lead_number
                      ? "'SF Mono','JetBrains Mono','Roboto Mono',Menlo,Consolas,monospace"
                      : undefined,
                    letterSpacing: task.lead.lead_number ? 0.4 : undefined,
                    bgcolor: "rgba(255,255,255,0.08)", color: "#fff",
                    border: "1px solid rgba(255,255,255,0.22)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.16)" },
                  }}
                />
              )}
            </Stack>
          </Box>

          {/* Right-side stats */}
          <Stack alignItems="flex-end" gap={0.6} sx={{ minWidth: 160 }}>
            <SummaryStat label="Due" value={summary.dueAt} />
            <SummaryStat label="Assigned" value={summary.assignee} />
            <SummaryStat label="Attempts" value={String(summary.attemptCount)} highlight />
            <SummaryStat label="Last activity" value={fmtRelative(lastActivity)} />
          </Stack>
        </Stack>
      </Box>

      <DialogContent dividers sx={{ p: 0, bgcolor: SOFT_BG }}>

        {/* ═════ SECTION 2 · ATTEMPTS TIMELINE (centerpiece) ════════ */}
        <Box sx={{ px: 3, py: 2.5 }}>
          <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
            <HistoryIcon sx={{ fontSize: 18, color: NAVY }} />
            <Typography variant="overline" sx={{ fontWeight: 800, color: NAVY, letterSpacing: 0.8, fontSize: 11 }}>
              Activity Timeline
            </Typography>
            <Box flex={1} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, fontWeight: 600 }}>
              {summary.attemptCount} attempt{summary.attemptCount !== 1 ? "s" : ""}
            </Typography>
          </Stack>

          {attemptsLoading ? (
            <Typography variant="caption" color="text.secondary">Loading attempts…</Typography>
          ) : attempts.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center", borderRadius: 2.5, border: `1px dashed ${BORDER_SOFT}`, bgcolor: "#fff" }}>
              <HistoryIcon sx={{ fontSize: 28, color: "#cbd5e1", mb: 0.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                No attempts yet
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                Use Log Next Attempt below to start the operational thread.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ position: "relative" }}>
              {/* Vertical rail */}
              <Box sx={{
                position: "absolute", left: 19, top: 6, bottom: 6, width: 2,
                bgcolor: "#e2e8f0", borderRadius: 1, zIndex: 0,
              }} />
              <Stack gap={1.1}>
                {attempts.map((a) => {
                  const om = outcomeMeta(a.follow_up_outcome ?? a.outcome);
                  const tm = TYPE_META[a.type] ?? TYPE_META.note;
                  return (
                    <Stack key={a.id} direction="row" gap={1.5} alignItems="flex-start" sx={{ position: "relative", zIndex: 1 }}>
                      {/* Dot avatar on the rail */}
                      <Avatar sx={{
                        width: 40, height: 40,
                        bgcolor: "#fff",
                        color: om?.color ?? tm.color,
                        border: `2.5px solid ${om?.color ?? tm.color}`,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                        flexShrink: 0,
                      }}>
                        {tm.icon}
                      </Avatar>
                      <Box
                        flex={1} minWidth={0}
                        sx={{
                          bgcolor: "#fff",
                          border: `1px solid ${BORDER_SOFT}`,
                          borderLeft: `4px solid ${om?.color ?? tm.color}`,
                          borderRadius: 2,
                          p: 1.4,
                          transition: "all 0.18s",
                          "&:hover": {
                            boxShadow: "0 4px 14px rgba(13,27,42,0.08)",
                            transform: "translateY(-1px)",
                          },
                        }}
                      >
                        <Stack direction="row" alignItems="center" gap={0.7} flexWrap="wrap">
                          <Typography variant="body2" sx={{ fontWeight: 800, color: NAVY, fontSize: 12.5, textTransform: "uppercase", letterSpacing: 0.4 }}>
                            {tm.label}
                          </Typography>
                          {om && (
                            <Chip size="small" icon={om.icon as any} label={om.label}
                              sx={{
                                height: 20, fontSize: 10.5, fontWeight: 800,
                                bgcolor: `${om.color}1A`, color: om.color,
                                border: `1px solid ${om.color}3A`,
                                "& .MuiChip-icon": { color: `${om.color} !important` },
                              }}
                            />
                          )}
                          <Box flex={1} />
                          <Tooltip title={formatDateTime(a.contacted_at)}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, fontWeight: 600 }}>
                              {fmtRelative(a.contacted_at)}
                            </Typography>
                          </Tooltip>
                        </Stack>
                        {a.notes && (
                          <Typography variant="body2" sx={{ display: "block", color: "#475569", fontSize: 12, lineHeight: 1.55, mt: 0.5, whiteSpace: "pre-wrap" }}>
                            {a.notes}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, display: "block", mt: 0.4, fontStyle: "italic" }}>
                          by {displayName(a.creator as any)} · {formatDateTime(a.contacted_at)}
                        </Typography>
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Box>

        {/* ═════ SECTION 3 · LOG NEW ATTEMPT (primary action) ═════════ */}
        {!isFinal && (
          <Box sx={{ px: 3, pb: 2.5 }}>
            <Box sx={{
              bgcolor: "#fff",
              border: `1px solid ${BORDER_SOFT}`,
              borderRadius: 2.5,
              p: 2.2,
              boxShadow: "0 4px 14px rgba(13,27,42,0.04)",
            }}>
              <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                <Box sx={{ width: 4, height: 18, borderRadius: 1, bgcolor: GOLD }} />
                <Typography variant="overline" sx={{ fontWeight: 800, color: NAVY, letterSpacing: 0.8, fontSize: 11.5 }}>
                  Log Next Attempt
                </Typography>
              </Stack>

              {/* Contact method — large segmented buttons */}
              <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, display: "block", mb: 0.8 }}>
                Contact method
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={attemptType}
                onChange={(_e, v) => v && setAttemptType(v)}
                fullWidth
                sx={{
                  mb: 1.8,
                  "& .MuiToggleButton-root": {
                    py: 1.1, textTransform: "none", fontWeight: 700, fontSize: 13,
                    border: `1px solid ${BORDER_SOFT}`,
                    color: "#64748b",
                    bgcolor: "#fff",
                    transition: "all 0.18s",
                    "&:hover": { bgcolor: "#f8fafc" },
                  },
                  "& .Mui-selected": {
                    bgcolor: `${NAVY} !important`,
                    color: "#fff !important",
                    borderColor: `${NAVY} !important`,
                    "&:hover": { bgcolor: `${NAVY_LIGHT} !important` },
                  },
                }}
              >
                {(["call","email","meeting","note"] as const).map((t) => {
                  const tm = TYPE_META[t];
                  return (
                    <ToggleButton key={t} value={t}>
                      <Stack direction="row" alignItems="center" gap={0.8}>
                        {tm.icon}
                        {tm.label}
                      </Stack>
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>

              {/* Outcome — large cards in a 3-col grid */}
              <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, display: "block", mb: 0.8 }}>
                Outcome
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: 0.8, mb: 1.6 }}>
                {FOLLOW_UP_OUTCOME_OPTIONS.map((o) => {
                  const meta   = OUTCOME_META[o.value];
                  const active = attemptOutcome === o.value;
                  return (
                    <Box
                      key={o.value}
                      onClick={() => setAttemptOutcome(active ? "" : o.value)}
                      sx={{
                        cursor: "pointer", userSelect: "none",
                        p: 1.05, borderRadius: 2,
                        bgcolor:   active ? `${o.color}14` : "#fff",
                        border:    `1.5px solid ${active ? o.color : BORDER_SOFT}`,
                        color:     active ? o.color : "#475569",
                        boxShadow: active ? `0 2px 10px ${o.color}33` : "none",
                        transition: "all 0.18s",
                        "&:hover": { borderColor: o.color, bgcolor: `${o.color}0A` },
                      }}
                    >
                      <Stack direction="row" alignItems="center" gap={0.7}>
                        <Box sx={{ color: o.color, display: "flex" }}>
                          {meta?.icon ?? <NotesIcon sx={{ fontSize: 16 }} />}
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 800, fontSize: 11.5, lineHeight: 1.1 }}>
                          {o.label}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                })}
              </Box>

              {/* Notes */}
              <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, display: "block", mb: 0.6 }}>
                Notes
              </Typography>
              <TextField
                size="small" fullWidth multiline minRows={2} maxRows={5}
                placeholder="Add details about this attempt…"
                value={attemptNotes}
                onChange={(e) => setAttemptNotes(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />

              {/* Smart guidance */}
              {guidance && (
                <Box sx={{
                  mt: 1.4, p: 1.2, borderRadius: 2,
                  bgcolor:
                    guidance.tone === "positive" ? "#ecfdf5" :
                    guidance.tone === "negative" ? "#fef2f2" : "#fffbeb",
                  border: `1px solid ${
                    guidance.tone === "positive" ? "#a7f3d0" :
                    guidance.tone === "negative" ? "#fecaca" : "#fde68a"
                  }`,
                }}>
                  <Stack direction="row" alignItems="flex-start" gap={1}>
                    <AutoAwesomeIcon sx={{ fontSize: 16, color:
                      guidance.tone === "positive" ? "#16a34a" :
                      guidance.tone === "negative" ? "#dc2626" : "#b45309",
                      mt: 0.2 }} />
                    <Box flex={1}>
                      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11.5, color:
                        guidance.tone === "positive" ? "#166534" :
                        guidance.tone === "negative" ? "#991b1b" : "#92400e",
                        display: "block", lineHeight: 1.5,
                      }}>
                        {guidance.text}
                      </Typography>
                      {guidance.suggestReschedule && (
                        <FormControlLabel
                          sx={{ mt: 0.5 }}
                          control={
                            <Checkbox size="small" checked={attemptScheduleNext}
                              onChange={(e) => setAttemptScheduleNext(e.target.checked)}
                            />
                          }
                          label={<Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11.5 }}>Reschedule the follow-up</Typography>}
                        />
                      )}
                      {attemptScheduleNext && guidance.suggestReschedule && (
                        <Box mt={0.6}>
                          <PremiumDateTimePicker label="New due date & time" value={attemptNextDue} onChange={setAttemptNextDue} />
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </Box>
              )}

              {/* Primary CTA — Log Attempt + optional Start */}
              <Stack direction="row" gap={1} mt={1.6} alignItems="center">
                <Button
                  variant="contained" size="medium"
                  startIcon={<PlayArrowIcon />}
                  disabled={logging || (attemptScheduleNext && !attemptNextDue)}
                  onClick={handleLogAttempt}
                  sx={{
                    textTransform: "none", fontWeight: 800, px: 2.4, py: 0.9,
                    borderRadius: 2,
                    bgcolor: NAVY,
                    boxShadow: `0 6px 16px ${NAVY}33`,
                    "&:hover": { bgcolor: NAVY_LIGHT, boxShadow: `0 8px 20px ${NAVY}40` },
                  }}
                >
                  {logging ? "Logging…" : "Log Attempt"}
                </Button>
                {task.status === "pending" && (
                  <Tooltip title="Move task to In Progress without logging an attempt">
                    <span>
                      <Button size="medium" variant="outlined" disabled={saving}
                        startIcon={<PlayArrowIcon />}
                        onClick={() => run({ status: "in_progress" })}
                        sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, borderColor: "#2563eb", color: "#2563eb", "&:hover": { borderColor: "#2563eb", bgcolor: "#eff6ff" } }}
                      >Start</Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          </Box>
        )}

        {/* ═════ SECTION 5 · TASK SETTINGS (administrative, collapsed) ═ */}
        {!isFinal && (
          <Box sx={{ px: 3, pb: 2.5 }}>
            <Accordion
              expanded={settingsOpen}
              onChange={(_e, v) => setSettingsOpen(v)}
              disableGutters
              elevation={0}
              sx={{
                bgcolor: "#fbfbfc",
                border: `1px solid ${BORDER_SOFT}`,
                borderRadius: 2,
                "&:before": { display: "none" },
                "&.Mui-expanded": { boxShadow: "0 2px 10px rgba(0,0,0,0.03)" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 0.8 } }}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <TuneIcon sx={{ fontSize: 16, color: "#64748b" }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11.5, color: "#64748b", letterSpacing: 0.6, textTransform: "uppercase" }}>
                    Task Settings
                  </Typography>
                  {isDirty && (
                    <Chip size="small" label="unsaved" sx={{ height: 18, fontSize: 9.5, fontWeight: 700, bgcolor: `${GOLD}22`, color: GOLD }} />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Stack gap={1.2}>
                  <PremiumDateTimePicker label="Due date & time" value={dueAt} onChange={setDueAt} />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select label="Priority" value={priority} onChange={(e) => setPriority(e.target.value as Task["priority"])}>
                      {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                        <MenuItem key={k} value={k}>
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: v.color }} />
                            {v.label}
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Assigned to</InputLabel>
                    <Select label="Assigned to" value={assignedTo as any} onChange={(e) => setAssignedTo(e.target.value as number | "")}>
                      <MenuItem value=""><em>Unassigned</em></MenuItem>
                      {(teamUsers ?? []).map((u: any) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.display_name || [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small" fullWidth multiline minRows={2} maxRows={5}
                    label="Task notes"
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {/* Completed/cancelled summary (read-only) */}
        {isFinal && (
          <Box sx={{ px: 3, pb: 2.5 }}>
            <Box sx={{ p: 2, bgcolor: "#fff", border: `1px solid ${BORDER_SOFT}`, borderRadius: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 }}>
                Resolution
              </Typography>
              <Stack gap={0.6} mt={0.6}>
                <SummaryRow label="Status" value={
                  <Chip size="small" label={statusStyle.label} sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: statusStyle.bg, color: statusStyle.fg }} />
                } />
                {task.status === "completed" && (
                  <>
                    <SummaryRow label="Completed at" value={summary.completedAt} />
                    <SummaryRow label="Completed by" value={summary.completer} />
                  </>
                )}
                {task.notes && <SummaryRow label="Task notes" value={<span style={{ whiteSpace: "pre-wrap" }}>{task.notes}</span>} />}
              </Stack>
            </Box>
          </Box>
        )}

        {errorMsg && (
          <Typography variant="caption" color="#dc2626" sx={{ display: "block", px: 3, pb: 1.5, fontWeight: 700 }}>
            {errorMsg}
          </Typography>
        )}
      </DialogContent>

      {/* ═════ SECTION 6 · FOOTER ACTIONS ═════════════════════════════ */}
      <DialogActions sx={{ px: 3, py: 1.8, justifyContent: "space-between", bgcolor: "#fff", borderTop: `1px solid ${BORDER_SOFT}` }}>
        <Stack direction="row" gap={1}>
          {!isFinal && (
            <>
              <Button
                size="medium" variant="contained" disabled={saving}
                startIcon={<CheckCircleIcon />}
                onClick={() => run({ status: "completed" })}
                sx={{
                  textTransform: "none", fontWeight: 800, borderRadius: 2, px: 2,
                  bgcolor: "#16a34a", boxShadow: "0 4px 12px rgba(22,163,74,0.25)",
                  "&:hover": { bgcolor: "#15803d", boxShadow: "0 6px 16px rgba(22,163,74,0.32)" },
                }}
              >Complete Follow-up</Button>
              <Button
                size="medium" variant="outlined" disabled={saving}
                startIcon={<BlockIcon />}
                onClick={() => run({ status: "cancelled" })}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, borderColor: "#fecaca", color: "#dc2626", "&:hover": { borderColor: "#dc2626", bgcolor: "#fef2f2" } }}
              >Cancel Follow-up</Button>
            </>
          )}
        </Stack>

        <Stack direction="row" gap={1}>
          {!isFinal && (
            <Button
              size="medium" variant="outlined" disabled={saving || !isDirty}
              onClick={() => run({
                due_at:      dueAt || undefined,
                priority,
                assigned_to: assignedTo === "" ? null : Number(assignedTo),
                notes:       notesDraft || undefined,
              } as any)}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, borderColor: NAVY, color: NAVY, "&:hover": { bgcolor: `${NAVY}08`, borderColor: NAVY } }}
            >
              Save Changes
            </Button>
          )}
          <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 600, color: "#64748b" }}>
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

/* ── Local sub-components (kept private to this file) ────────────────── */
function SummaryStat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <Box sx={{ textAlign: "right", lineHeight: 1.1 }}>
      <Typography variant="caption" sx={{ fontSize: 9.5, opacity: 0.65, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "#fff" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{
        fontSize: highlight ? 14 : 12,
        fontWeight: highlight ? 800 : 700,
        color: highlight ? GOLD : "#fff",
        lineHeight: 1.1,
        mt: 0.1,
      }}>
        {value}
      </Typography>
    </Box>
  );
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack direction="row" gap={1} alignItems="center">
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 105, fontSize: 11, fontWeight: 600 }}>{label}</Typography>
      <Box flex={1} sx={{ fontSize: 12, color: NAVY, fontWeight: 500 }}>{value}</Box>
    </Stack>
  );
}
